#!/usr/bin/env node

// Register the framework adapters (side-effect imports — must be before any provisioning code)
import '@augmented/core/provisioning/frameworks/claudecode/index.js';
// ADR-0046: opencode framework adapter (opt-in; claude-code stays default).
import '@augmented/core/provisioning/frameworks/opencode/index.js';

import { join } from 'node:path';
import { homedir } from 'node:os';
import { Command } from 'commander';
import { setJsonMode } from '../lib/globals.js';
import { whoamiCommand } from '../commands/whoami.js';
import { flagsResolveCommand } from '../commands/flags.js';
import { probeToolsCommand } from '../commands/probe-tools.js';
import {
  teamListCommand,
  teamCreateCommand,
  teamSwitchCommand,
} from '../commands/team.js';
import { initCommand } from '../commands/init.js';
import { lintCommand } from '../commands/lint.js';
import { channelsListCommand, channelsCheckCommand } from '../commands/channels.js';
import { channelSlackSetupCommand, channelSlackStatusCommand, channelSlackRemoveCommand } from '../commands/channel-slack.js';
import { channelBeamSetupCommand, channelBeamStatusCommand, channelBeamRemoveCommand } from '../commands/channel-beam.js';
import { deployCommand } from '../commands/deploy.js';
import { provisionCommand } from '../commands/provision.js';
import {
  impersonateConnectCommand,
  impersonateCurrentCommand,
  impersonateExitCommand,
  impersonateIntroduceCommand,
  autoExitExpiredImpersonation,
} from '../commands/impersonate.js';
import { driftCheckCommand, driftWatchCommand } from '../commands/drift.js';
import {
  hostListCommand,
  hostAssignCommand,
  hostUnassignCommand,
  hostAgentsCommand,
  hostRotateKeyCommand,
  hostDecommissionCommand,
  hostMaintenanceWindowCommand,
} from '../commands/host.js';
import { hostPairCommand } from '../commands/host-pair.js';
import {
  managerStartCommand,
  managerStopCommand,
  managerStatusCommand,
  managerInstallCommand,
  managerUninstallCommand,
  managerInstallSystemUnitCommand,
  managerUninstallSystemUnitCommand,
} from '../commands/manager.js';
import { managerWatchCommand } from '../commands/manager-watch.js';
import { agentShowCommand } from '../commands/agent.js';
import { agentRestoreTranscriptsCommand } from '../commands/agent-restore-transcripts.js';
import {
  kanbanRecurringAddCommand,
  kanbanRecurringListCommand,
  kanbanRecurringDisableCommand,
} from '../commands/kanban-recurring.js';
import { setupCommand } from '../commands/setup.js';
import {
  kanbanListCommand,
  kanbanAddCommand,
  kanbanMoveCommand,
  kanbanUpdateCommand,
  kanbanDoneCommand,
} from '../commands/kanban.js';
import { updateCommand, checkForUpdateOnStartup } from '../commands/update.js';
import { auditSubagentsCommand } from '../commands/audit-subagents.js';
import { auditMcpRenderCommand } from '../commands/audit-mcp-render.js';
import { auditSecretsCommand } from '../commands/audit-secrets.js';
import { anchorOnboardCommand } from '../commands/anchor.js';
import {
  integrationListCommand,
  integrationShowCommand,
  integrationProbeCommand,
  integrationInstallCommand,
  integrationUninstallCommand,
  integrationRequestsCommand,
  integrationApproveCommand,
  integrationDenyCommand,
  integrationEnrolCommand,
} from '../commands/integration.js';

declare const __CLI_VERSION__: string;

const cliVersion = typeof __CLI_VERSION__ !== 'undefined' ? __CLI_VERSION__ : 'dev';

const program = new Command();

program
  .name('agt')
  .description('Augmented CLI — agent provisioning and management')
  .version(cliVersion)
  .option('--json', 'Emit machine-readable JSON output (suppress spinners and colors)')
  .option('--skip-update-check', 'Skip the automatic update check on startup');

// ── Global pre-action hook ──────────────────────────────────────────────────

program.hook('preAction', async (thisCommand, actionCommand) => {
  const root = thisCommand.optsWithGlobals();
  if (root.json) {
    setJsonMode(true);
  }

  // ENG-5911: on any command, lazily auto-exit an expired impersonation
  // session (restore the operator's files + clear state) so a dead session
  // never lingers needing a manual `agt impersonate exit`. Skip the two
  // commands where it would be wrong or redundant:
  //   - `impersonate introduce`: the SessionStart hook — must never restore
  //     files / post /stop mid-session-boot (it already self-guards on expiry).
  //   - `impersonate exit`: redundant; it runs the cleanup itself.
  const segs: string[] = [];
  for (let c = actionCommand; c && c.parent; c = c.parent) segs.unshift(c.name());
  const cmdPath = segs.join(' ');
  if (cmdPath !== 'impersonate introduce' && cmdPath !== 'impersonate exit') {
    await autoExitExpiredImpersonation();
  }
});

// ── Auth commands ──────────────────────────────────────────────────────────

program
  .command('whoami')
  .description('Show the authenticated host, team, and user from AGT_API_KEY')
  .action(whoamiCommand);

// ── Feature flags ─────────────────────────────────────────────────────────

const flags = program
  .command('flags')
  .description('Inspect feature flags resolved on this host (ADR-0022)');

flags
  .command('resolve')
  .description('Print each flag’s effective value and source layer (env / heartbeat-cache / compiled default)')
  .option('--flag <key>', 'Resolve a single flag by key instead of all registered flags')
  .option('--host', 'Show host-cache diagnostics (unrecognised cached keys from a newer API)')
  .option('--config-dir <path>', 'Manager config dir holding flags-cache.json (default ~/.augmented)')
  .action((opts: { flag?: string; host?: boolean; configDir?: string }) => {
    flagsResolveCommand(opts);
  });

// ── Internal: on-demand session-tool-bind probe (ENG-7255) ─────────────────
// Machine-invoked over SSM by POST /admin/agents/:id/probe-tools. Probes one
// agent's integrations now and prints a single JSON object. Hidden: it's an
// operator-triggered host RPC, not a user-facing command.
program
  .command('probe-tools', { hidden: true })
  .description('Run a one-shot session-tool-bind probe for one agent and print JSON verdicts')
  .requiredOption('--agent-id <uuid>', 'Agent UUID to probe')
  .requiredOption('--code-name <name>', 'Agent code_name (resolves its project dir)')
  .option('--json', 'Emit JSON (always on; accepted for symmetry)')
  .action((opts: { agentId?: string; codeName?: string; json?: boolean }) => {
    return probeToolsCommand(opts);
  });

program
  .command('setup <token>')
  .description('One-command host setup: exchange provisioning token, configure env vars, verify, and start manager')
  .option(
    '--api-host <url>',
    // ENG-5831: required when AGT_HOST is not set in the shell — the setup
    // command no longer silently defaults to prod. Mirrors `agt impersonate
    // connect --api-host` (ENG-5773).
    'API host to exchange the provisioning token against. Takes precedence over AGT_HOST. Required when AGT_HOST is unset (e.g. https://test.api.staging.augmented.team for the test stage; https://api.augmented.team for prod).',
  )
  .action(setupCommand);

// ── Team commands ─────────────────────────────────────────────────────

const team = program
  .command('team')
  .description('Manage teams');

team
  .command('list')
  .description('List teams you belong to')
  .action(teamListCommand);

team
  .command('create <name>')
  .description('Create a new team and set it as active')
  .action(teamCreateCommand);

team
  .command('switch <slug>')
  .description('Switch the active team')
  .action(teamSwitchCommand);

// ── Impersonation commands ────────────────────────────────────────────────
// ENG-5688 (redesigned): operator-impersonates-agent v0. The webapp
// (ENG-5702) mints a redeem token; the operator pastes
// `agt impersonate connect <token>` to enter, `exit` to leave.
// All gated behind AGT_IMPERSONATE_ENABLED until the surrounding pieces
// (ENG-5689 channel-MCP refusal, ENG-5702 webapp button) land and the
// end-to-end smoke test runs against prod.

const impersonate = program
  .command('impersonate')
  .description(
    'Operate as a managed agent locally (experimental, gated by AGT_IMPERSONATE_ENABLED)',
  );

impersonate
  .command('connect <token>')
  .description(
    "Redeem a XXXX-XXXX token from the webapp, render the agent's CLAUDE.md + .mcp.json, swap them into the current project, and launch Claude Code (use --no-launch to skip the launch)",
  )
  .option(
    '--no-launch',
    "Skip launching Claude Code after the swap (operator manages their own session)",
  )
  .option(
    '--workdir',
    "Swap into an auto-provisioned dedicated directory (~/.augmented-impersonate/<agent>/workdir) instead of the current directory — required when the current directory is inside a git repo",
  )
  .option(
    '--api-host <url>',
    "Override the API host used to redeem the token. Defaults to https://api.augmented.team — AGT_HOST is intentionally ignored so a dev/Tailscale AGT_HOST doesn't accidentally 502 against a token minted on prod.",
  )
  .action(
    (token: string, opts: { launch?: boolean; workdir?: boolean; apiHost?: string }) =>
      // Commander negates --no-FLAG into opts.flag, so --no-launch arrives
      // as opts.launch === false. Translate to the command's noLaunch
      // option, defaulting to launching when the flag isn't passed.
      impersonateConnectCommand(token, {
        noLaunch: opts.launch === false,
        workdir: opts.workdir === true,
        apiHost: opts.apiHost,
      }),
  );

impersonate
  .command('current')
  .description('Print the active impersonation for this directory (or "none")')
  .option('--all', 'List every active impersonation session on this machine')
  .action((opts: { all?: boolean }) =>
    impersonateCurrentCommand({ all: opts.all === true }),
  );

impersonate
  .command('exit')
  .description("End this directory's impersonation, restore project files, and notify the server")
  .option('--all', 'Exit every active impersonation session on this machine')
  .action((opts: { all?: boolean }) =>
    impersonateExitCommand({ all: opts.all === true }),
  );

// Hidden: invoked by the SessionStart hook `connect` registers (ENG-5750),
// not meant to be run by operators directly. Prints the impersonated
// agent's introduction; silent no-op when no active impersonation.
impersonate
  .command('introduce', { hidden: true })
  .description("Print the impersonated agent's self-introduction (SessionStart hook target)")
  .action(impersonateIntroduceCommand);

// ── Agent commands ─────────────────────────────────────────────────────────

program
  .command('init')
  .description('Create a new agent (interactive wizard, or non-interactive with --name)')
  .option('--name <display-name>', 'Agent display name (triggers non-interactive mode)')
  .option('--code-name <code-name>', 'Agent code name (kebab-case, derived from --name if omitted)')
  .option('--description <text>', 'Short agent description')
  .option('--env <environment>', 'Environment: dev | stage | prod', 'dev')
  .option('--risk-tier <tier>', 'Risk tier: Low | Medium | High', 'Low')
  .option('--budget-type <type>', 'Budget type: tokens | dollars | both', 'tokens')
  .option('--budget-tokens <number>', 'Token budget limit', '10000')
  .option('--budget-dollars <number>', 'Dollar budget limit', '10')
  .option('--budget-window <window>', 'Budget window: daily | weekly | monthly', 'daily')
  .option('--budget-enforcement <mode>', 'Budget enforcement: block | throttle | alert | degrade', 'block')
  .option('--channels <list>', 'Comma-separated channel IDs')
  .option('--logging <mode>', 'Logging mode: redacted | hash-only | full-local', 'redacted')
  .action(initCommand);

program
  .command('lint')
  .argument('[path]', 'Path to agent directory (default: all agents in .augmented/)')
  .description('Lint CHARTER.md and TOOLS.md files')
  .action(lintCommand);

// ── Channel commands ───────────────────────────────────────────────────────

const channels = program
  .command('channels')
  .description('Manage and inspect channel configuration');

channels
  .command('list')
  .description('Print the full channel registry with security metadata')
  .action(channelsListCommand);

channels
  .command('check <agent>')
  .description('Resolve the effective channel list for an agent')
  .action(channelsCheckCommand);

const slack = channels
  .command('slack')
  .description('Manage Slack channel configuration');

slack
  .command('setup <agent-code-name>')
  .description('Configure Slack bot scopes, generate manifest, and store credentials')
  .option('--preset <preset>', 'Scope preset: minimal | standard | full')
  .option('--scopes <scopes>', 'Comma-separated list of Slack bot scopes')
  .option('--skip-create', 'Skip Slack CLI app creation step')
  .option('--bot-token <token>', 'Slack bot token (xoxb-…)')
  .option('--signing-secret <secret>', 'Slack signing secret')
  .option('--config-token <token>', 'Slack app configuration token (xoxe-…) for API-based app creation')
  .action(channelSlackSetupCommand);

slack
  .command('status <agent-code-name>')
  .description('Show current Slack configuration for an agent')
  .action(channelSlackStatusCommand);

slack
  .command('remove <agent-code-name>')
  .description('Remove Slack bot configuration for an agent')
  .action(channelSlackRemoveCommand);

const beam = channels
  .command('beam')
  .description('Manage Beam Protocol channel configuration');

beam
  .command('setup <agent-code-name>')
  .description('Generate Beam identity, register in directory, and configure channel')
  .option('--directory-url <url>', 'Beam directory URL (default: hosted)')
  .option('--skip-register', 'Skip directory registration')
  .option('--auto-publish', 'Auto-publish capabilities from TOOLS.md', true)
  // ENG-8540: required to replace an existing identity in --json mode, where
  // there is nobody to answer the confirmation prompt.
  .option('--force', 'Replace an existing Beam identity (generates a NEW keypair)')
  .action(channelBeamSetupCommand);

beam
  .command('status <agent-code-name>')
  .description('Show Beam configuration and directory status')
  .action(channelBeamStatusCommand);

beam
  .command('remove <agent-code-name>')
  .description('Remove Beam identity for an agent')
  .action(channelBeamRemoveCommand);

// ── Provision commands ─────────────────────────────────────────────────────

program
  .command('provision <code-name>')
  .description('Provision an agent: build config, generate files, store snapshot')
  .option('--target <target>', 'Deployment target', 'local_docker')
  .option('--output <dir>', 'Output directory for generated files')
  .option('--dry-run', 'Print what would be generated without writing files')
  .action(provisionCommand);

// ── Deploy commands ────────────────────────────────────────────────────────

program
  .command('deploy')
  .description('Generate deployment config from a template')
  .option('--template <id>', 'Template ID (triggers non-interactive mode)')
  .option('--port <number>', 'Base port for gateway', '9000')
  .action(deployCommand);

// ── Drift commands ────────────────────────────────────────────────────────

const drift = program
  .command('drift')
  .description('Detect configuration drift from provisioned state');

drift
  .command('check <code-name>')
  .description('Compare live state against provisioned snapshot')
  .option('--json', 'Output as JSON')
  .option('--audit', 'Run security audit')
  .option('--config <path>', 'Path to config file')
  .action(driftCheckCommand);

drift
  .command('watch <code-name>')
  .description('Continuously monitor for drift')
  .option('--interval <seconds>', 'Check interval in seconds', '60')
  .option('--webhook <url>', 'POST webhook URL on new findings')
  .option('--json', 'Output as JSON')
  .option('--config <path>', 'Path to config file')
  .action(driftWatchCommand);

// ── Host commands ─────────────────────────────────────────────────────────

const host = program
  .command('host')
  .description('Manage hosts and their API keys');

host
  .command('list')
  .description('List hosts in the active team')
  .action(hostListCommand);

host
  .command('assign <host-name> <agent-code-names...>')
  .description('Assign agents to a host')
  .option('--force', 'Reassign agents already assigned to another host')
  .action(hostAssignCommand);

host
  .command('unassign <host-name> <agent-code-names...>')
  .description('Unassign agents from a host')
  .action(hostUnassignCommand);

host
  .command('agents [host-name]')
  .description('List agents assigned to a host (omit name to auto-resolve from AGT_API_KEY)')
  .action(hostAgentsCommand);

host
  .command('rotate-key <host-name>')
  .description('Rotate the API key for a host (revokes the old key)')
  .action(hostRotateKeyCommand);

host
  .command('decommission <host-name>')
  .description('Decommission a host (revokes key, marks inactive)')
  .action(hostDecommissionCommand);

host
  .command('maintenance-window <host-name>')
  .description('View or set the host maintenance window for restart-causing updates')
  .option('--start <HH:MM>', 'Window start, 24h local time (e.g. 01:00)')
  .option('--end <HH:MM>', 'Window end, 24h local time (e.g. 02:00)')
  .option('--tz <IANA>', 'Window timezone (e.g. Australia/Sydney)')
  .option('--clear', 'Clear the override and follow the default 01:00–02:00')
  .action(hostMaintenanceWindowCommand);

host
  .command('pair <host-name>')
  .description('Start an SSM port-forward + shell to re-authenticate Claude Code on an EC2 host')
  .option('--port <port>', 'Local port to forward for the OAuth callback', '54545')
  // Commander produces `opts.shell` (boolean, default true) for `--no-shell`.
  // Adapt to the hostPairCommand signature which expects `opts.noShell`.
  .option('--no-shell', 'Start the tunnel only; do not open an interactive shell')
  .action((hostName: string, opts: { port?: string; shell?: boolean }) =>
    hostPairCommand(hostName, {
      port: opts.port,
      noShell: opts.shell === false,
    })
  );

// ── Manager commands ──────────────────────────────────────────────────────

const manager = program
  .command('manager')
  .description('Host config sync daemon — keeps local agent files in sync with API');

manager
  .command('start')
  .description('Start the manager daemon (polls API for config changes and detects local drift)')
  .option('--interval <seconds>', 'Poll interval in seconds (min 5)', '10')
  .option('--config-dir <dir>', 'Config directory for agent files', join(homedir(), '.augmented'))
  .option('--supervise', 'Wrap the manager in a respawn-on-clean-exit loop so auto-upgrades can restart it transparently (ENG-4488)', false)
  .action(managerStartCommand);

manager
  .command('stop')
  .description('Stop the running manager daemon')
  .option('--config-dir <dir>', 'Config directory for agent files', join(homedir(), '.augmented'))
  .action(managerStopCommand);

manager
  .command('status')
  .description('Show the current manager daemon status and discovered agents')
  .option('--config-dir <dir>', 'Config directory for agent files', join(homedir(), '.augmented'))
  .action(managerStatusCommand);

manager
  .command('watch')
  .description('Live TUI dashboard — per-agent boxes, drill-in, log tail. Read-only (ENG-4555).')
  .option('--config-dir <dir>', 'Config directory for agent files', join(homedir(), '.augmented'))
  .option('--no-tui', 'Skip the TUI and stream the manager log to stdout instead (CI / scripts)')
  .action(managerWatchCommand);

manager
  .command('install')
  .description('Install OS-level supervisor (launchd LaunchAgent on macOS) so the manager auto-restarts after crash, reboot, or self-update (ENG-4593)')
  .option('--interval <seconds>', 'Poll interval in seconds (min 5)', '10')
  .option('--config-dir <dir>', 'Config directory for agent files', join(homedir(), '.augmented'))
  .action(managerInstallCommand);

manager
  .command('uninstall')
  .description('Remove the OS-level supervisor (launchctl unload + delete plist on macOS). Idempotent.')
  .action(managerUninstallCommand);

manager
  .command('install-system-unit')
  .description('Install a system-level systemd unit (Linux, root) so the manager auto-starts on every boot. For headless EC2 hosts — survives reboot without `loginctl enable-linger`. (ENG-4706)')
  .option('--interval <seconds>', 'Poll interval in seconds (min 5)', '10')
  .option('--config-dir <dir>', 'Config directory for agent files (defaults to /root/.augmented or /home/<user>/.augmented)')
  .option('--user <name>', 'Unix user the manager runs as', 'root')
  .action(managerInstallSystemUnitCommand);

manager
  .command('uninstall-system-unit')
  .description('Remove the system-level systemd unit (Linux, root). Idempotent. (ENG-4706)')
  .action(managerUninstallSystemUnitCommand);

// ── Agent inspect commands ────────────────────────────────────────────────

const agent = program
  .command('agent')
  .description('Inspect and manage agents');

agent
  .command('restore-transcripts <code-name>')
  .description(
    "Place a migrated agent's archived transcripts on this host and re-pin its conversation (ENG-9567)",
  )
  .option('--from <host>', 'Source host to restore from — its name or uuid')
  .option('--bucket <bucket>', 'Source bucket, when no address is recorded for the host')
  .option('--key-prefix <prefix>', 'Source key prefix, when no address is recorded for the host')
  .option('--region <region>', 'Bucket region')
  .option('--timezone <iana>', "The agent's IANA timezone, so the restored pin is dated as the spawner will read it")
  .option('--json', 'Emit the result as JSON')
  .action(agentRestoreTranscriptsCommand);

agent
  .command('show <code-name>')
  .description("Display an agent's provisioned configuration")
  .option('--config-dir <dir>', 'Config directory', join(homedir(), '.augmented'))
  .option('--all-channels', 'Show all channels (including disabled)')
  .action(agentShowCommand);

// ── Kanban commands ──────────────────────────────────────────────────────

const kanban = program
  .command('kanban')
  .description('Manage agent kanban boards');

kanban
  .command('list')
  .description('List kanban board items for an agent')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .action(kanbanListCommand);

kanban
  .command('add <title>')
  .description('Add a new item to an agent kanban board')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .option('--priority <1|2|3>', 'Priority: 1=high, 2=medium, 3=low', '2')
  .option('--status <status>', 'Initial status: backlog | todo | in_progress', 'todo')
  .option('--description <text>', 'Item description')
  .option('--estimate <minutes>', 'Estimated time in minutes')
  .option('--deliverable <text>', 'Expected output/deliverable')
  .action(kanbanAddCommand);

kanban
  .command('move <title-or-id> <status>')
  .description('Move a kanban item to a different status')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .option('--notes <text>', 'Progress notes')
  .action(kanbanMoveCommand);

kanban
  .command('update <title-or-id>')
  .description('Update notes or result on a kanban item')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .option('--notes <text>', 'Progress notes')
  .option('--result <text>', 'Result/output produced')
  .action(kanbanUpdateCommand);

kanban
  .command('done <title-or-id>')
  .description('Mark a kanban item as done')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .option('--result <text>', 'What was produced/delivered')
  .option('--notes <text>', 'Completion notes')
  .action(kanbanDoneCommand);

const recurring = kanban
  .command('recurring')
  .description('Manage recurring kanban tasks');

recurring
  .command('add <title>')
  .description('Create a recurring kanban task')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .requiredOption('--every <schedule>', 'Schedule: "every Monday at 9am", "daily at 8:30am", "every 2 hours", or cron')
  .option('--priority <1|2|3>', 'Priority: 1=high, 2=medium, 3=low', '2')
  .option('--description <text>', 'Task description')
  .option('--estimate <minutes>', 'Estimated time in minutes')
  .option('--deliverable <text>', 'Expected output/deliverable')
  .option('--timezone <tz>', 'Timezone (default: UTC)')
  .action(kanbanRecurringAddCommand);

recurring
  .command('list')
  .description('List recurring kanban templates for an agent')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .action(kanbanRecurringListCommand);

recurring
  .command('disable <title-or-id>')
  .description('Disable a recurring kanban template')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .action(kanbanRecurringDisableCommand);

// ── Anchor Browser commands ───────────────────────────────────────────────
// ENG-7749: one-time live-view onboarding for a persistent Anchor profile. The
// operator logs into the target site by hand; only the non-secret profile name
// is persisted on our side.

const anchor = program
  .command('anchor')
  .description('Anchor Browser persistent-profile tools');

anchor
  .command('onboard <profile>')
  .description('One-time live-view login to save an authenticated Anchor profile (agent then resumes it with no re-auth)')
  // No --api-key flag on purpose: a secret on the command line leaks via shell
  // history and process listings. The key is read from ANCHOR_API_KEY only.
  .option('--api-base <url>', 'Override the Anchor API base URL')
  .option('--no-sticky-ip', 'Do not request a dedicated sticky IP for the session')
  .action((profile: string, opts: { apiBase?: string; stickyIp?: boolean }) =>
    anchorOnboardCommand(profile, { apiBase: opts.apiBase, stickyIp: opts.stickyIp }),
  );

// ── Integration commands ─────────────────────────────────────────────────

const integration = program
  .command('integration')
  .description('Manage integrations — install, configure, and control permission scopes');

integration
  .command('list')
  .description('List available integrations for the active team')
  .action(integrationListCommand);

integration
  .command('show <slug>')
  .description('Show integration details including permission scopes')
  .action(integrationShowCommand);

integration
  .command('probe <agent> <slug>')
  .description("Run a FRESH host-side connectivity probe for an agent's integration (live verdict, not the cached status)")
  .action(integrationProbeCommand);

integration
  .command('install <slug>')
  .description('Install an integration on an agent')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .option('--scopes <scopes>', 'Comma-separated scope IDs to grant')
  .action(integrationInstallCommand);

integration
  .command('uninstall <slug>')
  .description('Remove an integration from an agent')
  .requiredOption('--agent <code-name>', 'Agent code name')
  .action(integrationUninstallCommand);

integration
  .command('requests')
  .description('List pending scope approval requests for the team')
  .action(integrationRequestsCommand);

integration
  .command('approve <request-id>')
  .description('Approve a scope request')
  .option('--notes <text>', 'Review notes')
  .action(integrationApproveCommand);

integration
  .command('deny <request-id>')
  .description('Deny a scope request')
  .option('--reason <text>', 'Denial reason')
  .action(integrationDenyCommand);

integration
  .command('enrol')
  .description('Enrol an integration with an API key or webhook secret (no OAuth flow)')
  .requiredOption('--def <code-name>', 'Integration definition code_name (e.g. resend, xero)')
  .requiredOption('--scope <org|team|agent>', 'Install scope')
  .option('--api-key <value>', 'API key (sets auth_type=api_key)')
  .option('--access-token <value>', 'Bearer access token (sets auth_type=oauth2)')
  .option('--webhook-secret <value>', 'Webhook signing secret (sets auth_type=webhook)')
  .option('--display-name <name>', 'Override display name (defaults to the definition’s display_name)')
  .option('--agent <code-name>', 'Agent code name (required for agent scope)')
  .action(integrationEnrolCommand);

// ── Update command ───────────────────────────────────────────────────────

program
  .command('update')
  .description('Check for and install CLI updates')
  .option('--force', 'Update even if manager or agent sessions are running (also overrides a version pin)')
  .option('--version <version>', 'Install an exact version (may be a downgrade), bypassing the latest check')
  .action(updateCommand);

// ENG-5897: regression check for sub-agent MCP-binding gaps. Walks
// `~/.claude/agents/`, `~/.claude/plugins/<plugin>/agents/`, and the
// current dir's `.claude/agents/` and reports sub-agents whose
// `tools:` allowlist would silently block every `mcp__*` tool on
// dispatch.
const audit = program
  .command('audit')
  .description('Operator audits — sub-agent MCP bindings, etc.');
audit
  .command('subagents')
  .description('Find sub-agents whose tools allowlist would block mcp__* calls on dispatch (ENG-5897)')
  .option('--strict', 'Exit with code 2 if any findings (suitable for CI gating)')
  .option('--json', 'Emit findings as JSON to stdout')
  .action(auditSubagentsCommand);

// ENG-5922: runtime check that every managed agent's rendered
// sub-agent .md `tools:` allowlist covers every server in its actual
// `.mcp.json`. Catches the staleness Don found on his host (.md had 3
// wildcards, .mcp.json had 9 keys → sub-agent calls failed with "No
// such tool available" even when the tool was provisioned).
audit
  .command('mcp-render')
  .description('Find managed agents whose subagent .md tools allowlist is stale vs .mcp.json (ENG-5922)')
  .option('--strict', 'Exit with code 2 if any findings (suitable for CI gating)')
  .option('--json', 'Emit findings as JSON to stdout')
  .action(auditMcpRenderCommand);

// ENG-5901 (ADR-0018 Phase 1): operator-side scan for literal secrets in
// `~/.augmented/<agent>/{provision,project}/.mcp.json` (should be `${VAR}`
// placeholders) and world-readable secret files (.mcp.json / .env.integrations
// should be 0600). Companion to the contributor-side runtime lint.
audit
  .command('secrets')
  .description('Find literal secrets in .mcp.json and world-readable secret files (ENG-5901)')
  .option('--strict', 'Exit with code 2 if any findings (suitable for CI gating)')
  .option('--json', 'Emit findings as JSON to stdout')
  .action(auditSecretsCommand);

// ── Parse & run ────────────────────────────────────────────────────────────

// Non-blocking startup update check (fire-and-forget, resolves after parse)
const skipUpdateCheck =
  process.argv.includes('--skip-update-check') ||
  process.argv.includes('--json') ||
  process.argv[2] === 'update';

if (!skipUpdateCheck) {
  // Fire the check but don't await — it prints to stderr if an update is found
  checkForUpdateOnStartup().catch(() => {});
}

// parseAsync (not parse) so the async pre-action hook — which may auto-exit an
// expired impersonation session — is awaited before the command's action runs.
program.parseAsync().catch((err) => {
  console.error(err instanceof Error ? err.message : String(err));
  process.exitCode = 1;
});
